import axiosInstance from '../../../axiosInstance'
import apiEndpoints from '../../../api'

// GET /api/login-history/attendance — one row per login/logout event with
// location + geofence status. Non-super-admins must scope to a society; the
// active society is also sent globally via the X-Society-Id header, but we
// pass society_id explicitly to match the other report requests.
export const getAttendanceReportReq = (action) => {
    const searchQuery = action?.data || {};
    const params = {
        page: searchQuery.page || 1,
        limit: searchQuery.limit || 20,
    };

    if (searchQuery.society_id) params.society_id = searchQuery.society_id;
    if (searchQuery.user_id) params.user_id = searchQuery.user_id;
    if (searchQuery.action_type) params.action = searchQuery.action_type;
    if (searchQuery.within_geofence !== undefined && searchQuery.within_geofence !== '') {
        params.within_geofence = searchQuery.within_geofence;
    }
    if (searchQuery.from) params.from = searchQuery.from;
    if (searchQuery.to) params.to = searchQuery.to;

    return axiosInstance.get(apiEndpoints.SOCIETY_GET_ATTENDANCE_REPORT_API, { params });
}

// ---------------------------------------------------------------------------
// Employee Attendance (shift punch in/out) — /api/attendance/*
// Self-service endpoints, hard-scoped to the token's employee record. Society
// context rides on the global X-Society-Id header set by axiosInstance.
// ---------------------------------------------------------------------------

// GET /api/attendance/enums — valid punch types / statuses / day statuses for
// filter dropdowns, so the UI never hardcodes them.
export const getAttendanceEnumsReq = () =>
    axiosInstance.get(apiEndpoints.SOCIETY_ATTENDANCE_ENUMS_API);

// GET /api/attendance/me/status — the pre-punch check. Coordinates are
// optional; when present the response includes live geofence distance so the
// screen can explain a disabled button before the user taps it.
export const getMyAttendanceStatusReq = (action) => {
    const data = action?.data || {};
    const params = {};
    if (data.latitude != null && data.longitude != null) {
        params.latitude = data.latitude;
        params.longitude = data.longitude;
    }
    return axiosInstance.get(apiEndpoints.SOCIETY_ATTENDANCE_MY_STATUS_API, { params });
}

// POST /api/attendance/punch — toggle punch. `client_request_id` is the
// per-tap idempotency key: a network retry of the same tap returns the
// original punch (HTTP 200 + duplicate:true) instead of double-punching.
// Sent as multipart only when a selfie file is attached; plain JSON otherwise.
//
// `work_logs` (OUT punches only) is an array of { work_type, description,
// status } rows. In multipart every field arrives as a string, so it goes as
// JSON there and the BE's parseWorkLogs middleware parses it back.
export const punchAttendanceReq = (action) => {
    const data = action?.data || {};
    const workLogs = Array.isArray(data.work_logs) && data.work_logs.length > 0
        ? data.work_logs
        : null;

    const fields = {
        client_request_id: data.client_request_id,
        client_punch_at: data.client_punch_at,
        source: data.source || 'WEB',
        device_type: data.device_type,
        platform: data.platform,
        app_version: data.app_version,
        notes: data.notes,
    };
    // latitude/longitude must be sent together or not at all (BE 400s otherwise)
    if (data.latitude != null && data.longitude != null) {
        fields.latitude = data.latitude;
        fields.longitude = data.longitude;
        if (data.accuracy_meters != null) fields.accuracy_meters = data.accuracy_meters;
    }

    if (data.selfie) {
        const form = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') form.append(key, value);
        });
        form.append('selfie', data.selfie);
        if (workLogs) form.append('work_logs', JSON.stringify(workLogs));
        return axiosInstance.post(apiEndpoints.SOCIETY_ATTENDANCE_PUNCH_API, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    const body = {};
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') body[key] = value;
    });
    if (workLogs) body.work_logs = workLogs;
    return axiosInstance.post(apiEndpoints.SOCIETY_ATTENDANCE_PUNCH_API, body);
}

// GET /api/attendance/me — my punch events, newest first.
export const getMyAttendancePunchesReq = (action) => {
    const data = action?.data || {};
    const params = {
        page: data.page || 1,
        limit: data.limit || 20,
    };
    if (data.from) params.from = data.from;
    if (data.to) params.to = data.to;
    if (data.punch_type) params.punch_type = data.punch_type;
    if (data.status) params.status = data.status;
    if (data.within_geofence !== undefined && data.within_geofence !== '') {
        params.within_geofence = data.within_geofence;
    }
    return axiosInstance.get(apiEndpoints.SOCIETY_ATTENDANCE_MY_PUNCHES_API, { params });
}

// ---------------------------------------------------------------------------
// Admin attendance management — permission-gated, society-scoped via the
// X-Society-Id header the axios instance sends on every request.
// ---------------------------------------------------------------------------

const cleanParams = (obj) => {
    const params = {};
    Object.entries(obj).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params[key] = value;
    });
    return params;
};

// GET /api/attendance/daily — one row per employee per date (attendance.read_all)
export const getAdminAttendanceDailyReq = (action) => {
    const d = action?.data || {};
    return axiosInstance.get(apiEndpoints.SOCIETY_ATTENDANCE_DAILY_API, {
        params: cleanParams({
            employee_id: d.employee_id,
            department: d.department,
            search: d.search,
            from: d.from,
            to: d.to,
            day_status: d.day_status,
            current_state: d.current_state,
            has_pending_approval: d.has_pending_approval,
            out_of_fence_only: d.out_of_fence_only,
            page: d.page || 1,
            limit: d.limit || 50,
        }),
    });
}

// GET /api/attendance/summary — dashboard tiles for one date (attendance.read_all)
export const getAdminAttendanceSummaryReq = (action) => {
    const d = action?.data || {};
    return axiosInstance.get(apiEndpoints.SOCIETY_ATTENDANCE_ADMIN_SUMMARY_API, {
        params: cleanParams({ date: d.date }),
    });
}

// GET /api/attendance — punch-event grid / audit trail (attendance.read_all)
export const getAdminAttendancePunchesReq = (action) => {
    const d = action?.data || {};
    return axiosInstance.get(apiEndpoints.SOCIETY_ATTENDANCE_ADMIN_PUNCHES_API, {
        params: cleanParams({
            employee_id: d.employee_id,
            department: d.department,
            search: d.search,
            from: d.from,
            to: d.to,
            punch_type: d.punch_type,
            status: d.status,
            geofence_status: d.geofence_status,
            within_geofence: d.within_geofence,
            is_manual: d.is_manual,
            page: d.page || 1,
            limit: d.limit || 20,
        }),
    });
}

// POST /api/attendance/manual — record a missed punch on an employee's behalf
// (attendance.manage). To fix a WRONG punch use updateAttendancePunchReq.
export const createManualPunchReq = (action) => {
    const d = action?.data || {};
    return axiosInstance.post(apiEndpoints.SOCIETY_ATTENDANCE_MANUAL_PUNCH_API, cleanParams({
        employee_id: d.employee_id,
        punch_type: d.punch_type,
        punch_at: d.punch_at,
        manual_reason: d.manual_reason,
        notes: d.notes,
    }));
}

// PATCH /api/attendance/:id/approve — clear a flagged punch (attendance.approve)
export const approveAttendancePunchReq = (action) => {
    const d = action?.data || {};
    const url = apiEndpoints.SOCIETY_ATTENDANCE_APPROVE_PUNCH_API.replace(':id', d.punch_id);
    return axiosInstance.patch(url, cleanParams({ notes: d.notes }));
}

// PATCH /api/attendance/:id/reject — void a punch; notes required (attendance.approve)
export const rejectAttendancePunchReq = (action) => {
    const d = action?.data || {};
    const url = apiEndpoints.SOCIETY_ATTENDANCE_REJECT_PUNCH_API.replace(':id', d.punch_id);
    return axiosInstance.patch(url, { notes: d.notes });
}

// PATCH /api/attendance/:punch_id — admin punch correction (attendance.manage).
// Send only the fields being changed (punch_type / punch_at / notes);
// update_reason is always required and lands in the audit trail.
export const updateAttendancePunchReq = (action) => {
    const data = action?.data || {};
    const body = { update_reason: data.update_reason };
    if (data.punch_type) body.punch_type = data.punch_type;
    if (data.punch_at) body.punch_at = data.punch_at;
    if (data.notes !== undefined && data.notes !== null) body.notes = data.notes;
    if (data.society_id) body.society_id = data.society_id;

    const url = apiEndpoints.SOCIETY_UPDATE_ATTENDANCE_PUNCH_API.replace(':id', data.punch_id);
    return axiosInstance.patch(url, body);
}

// GET /api/attendance/me/summary — one row per day + period totals.
export const getMyAttendanceSummaryReq = (action) => {
    const data = action?.data || {};
    const params = {};
    if (data.from) params.from = data.from;
    if (data.to) params.to = data.to;
    if (data.day_status) params.day_status = data.day_status;
    if (data.page) params.page = data.page;
    if (data.limit) params.limit = data.limit;
    return axiosInstance.get(apiEndpoints.SOCIETY_ATTENDANCE_MY_SUMMARY_API, { params });
}
